<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import ability from '@/plugins/casl/ability'
import {
  $crApprovalFileAttachmentAnnotationList,
  $crFileAttachmentAnnotationList,
  $crSubtaskFileAttachmentAnnotationList,
  $getCrApprovalFileAttachment,
  $getCrFileAttachment,
  $getCrSubtaskFileAttachment
} from '@/api/changeRequests'
import { downloadFileUsingLink } from '@/helpers/utils/downloadAllFilesUsingLinks'
import { getFileExtension } from '@/helpers/utils/checkFileUrlType/getFileExtension'
import { isImageFile } from '@/helpers/utils/checkFileUrlType/isImageFile'

interface IProps {
  modelValue: boolean,
  fileAttachmentId: number | null,
  crItemType: 'cr' | 'crApproval' | 'crSubtask',
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  fileAttachmentId: null,
  crItemType: 'cr',
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onSubmit'): void
}

const emit = defineEmits<Emit>()

// // Define the type for the injected method
// type GetSignMethod = () => void
//
// // Inject the method provided by the parent
// const executeGetSign = inject<GetSignMethod>('executeGetSign')

const fileAttachment = ref<any>(null)
let fileContextVersion = 0
const hasOrdinaryAccess = computed(() => fileAttachment.value?.has_ordinary_access === true)

const annotations = ref<any[]>([])

const fileCurrentPage = ref<number>(1)
const fileLoading = ref<boolean>(true)
const loading = ref<boolean>(false)

const pdfScale = ref<number>(100)

const showAnnotationCommentDialog = ref<boolean>(false)
const parentAnnotation = ref<any>(null)

const isCanAddAnnotation = computed(() => {
  // return ability.can('add', 'crfileannot') && props.cr.is_active
  return hasOrdinaryAccess.value
})

const isDisplayableFileType = computed<boolean>(() => {
  const fileUrl = fileAttachment.value?.file || null
  if (!fileUrl) return false

  const extension = getFileExtension(fileUrl)
  return extension === 'pdf' || isImageFile(fileUrl)
})

const headerHeight = computed(() => {
  // If dialog is shown
  if (!!props.modelValue) {
    const headerEl = document.querySelector('.cr-file-review-dialog.v-card-title') as HTMLElement
    return headerEl?.offsetHeight || 48.5
  }
  else {
    return 48.5
  }
})

const closeDialog = () => {
  emit('update:modelValue', false)
}

const onClose = async () => {
  emit('onClose')
}

const setHover = (annotation: any, status: boolean) => {
  annotation['hover'] = status
}

const getFileAttachment = async () => {
  const contextVersion = fileContextVersion
  if (!props?.fileAttachmentId) return

  loading.value = true

  let res
  try {
    switch (props.crItemType) {
      case 'crApproval':
        res = await $getCrApprovalFileAttachment(props.fileAttachmentId)
        break
      case 'crSubtask':
        res = await $getCrSubtaskFileAttachment(props.fileAttachmentId)
        break
      default:
        res = await $getCrFileAttachment(props.fileAttachmentId)
        break
    }

    if (contextVersion !== fileContextVersion) return
    fileAttachment.value = res.data
    annotations.value = res.data?.annotations || []
  } catch (e) {
    console.log(e)
  } finally {
    if (contextVersion === fileContextVersion) loading.value = false
  }
}

const getAnnotations = async () => {
  const contextVersion = fileContextVersion
  if (!props?.fileAttachmentId) return

  loading.value = true

  const payload = {
    file_attachment: props.fileAttachmentId
  }

  let res
  try {
    switch (props.crItemType) {
      case 'crApproval':
        res = await $crApprovalFileAttachmentAnnotationList(payload)
        break
      case 'crSubtask':
        res = await $crSubtaskFileAttachmentAnnotationList(payload)
        break
      default:
        res = await $crFileAttachmentAnnotationList(payload)
        break
    }

    if (contextVersion !== fileContextVersion) return
    annotations.value = res.data.results
  } catch (e) {
    console.log(e)
  } finally {
    if (contextVersion === fileContextVersion) loading.value = false
  }
}

const onSubmitAnnotationComment = async () => {
  parentAnnotation.value = null
  await getAnnotations()
}

watch(() => [props.modelValue, props.fileAttachmentId, props.crItemType], () => {
  ++fileContextVersion
  fileAttachment.value = null
  annotations.value = []
  parentAnnotation.value = null
  showAnnotationCommentDialog.value = false
  loading.value = false
  // On show dialog
  if (props.modelValue) {
    fileCurrentPage.value = 1
    fileLoading.value = true
    getFileAttachment()
  }
  // On hide dialog
  else {
    // if (executeGetSign) {
    //   executeGetSign()
    // }
  }
})
</script>

<template>
  <div>
    <CrFileReviewAnnotationCommentDialog
      v-model="showAnnotationCommentDialog"
      :parent-annotation="parentAnnotation"
      :has-ordinary-access="hasOrdinaryAccess"
      :cr-item-type="crItemType"
      @onSubmit="onSubmitAnnotationComment"
    />
    <CustomVDialog
      :model-value="modelValue"
      fullscreen
      persistent
      class="default-dialog cr-file-review-dialog"
      @after-leave="onClose"
      @update:modelValue="emit('update:modelValue')"
    >
      <VBtn
        variant="elevated"
        density="compact"
        icon="tabler-x"
        :disabled="!!loading"
        class="v-dialog-close-btn"
        @click="closeDialog"
      />

      <VCard
        :style="{
          '--header-height': `${headerHeight}px`,
        }"
      >
        <template #title>
          <div class="d-flex align-center justify-space-between flex-wrap mt-1">
            <div class="d-flex align-center flex-wrap">
              File Review
              <VBtn
                v-if="fileAttachment?.file"
                size=""
                class="pa-1 ma-1"
                :href="fileAttachment?.file"
                target="_blank"
              >
                <VIcon
                  size="20"
                  icon="tabler-external-link"
                />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="1"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    File Link/Preview
                  </span>
                </VMenu>
              </VBtn>
              <VBtn
                v-if="fileAttachment?.file"
                size=""
                class="pa-1 ma-1"
                @click="downloadFileUsingLink(fileAttachment?.file)"
              >
                <VIcon
                  size="20"
                  icon="tabler-download"
                />
                <VMenu
                  activator="parent"
                  location="top center"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="1"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    Download File
                  </span>
                </VMenu>
              </VBtn>
              <VAlert
                v-if="isCanAddAnnotation && isDisplayableFileType"
                show
                variant="tonal"
                color="info"
                class="text-body-2 py-1 ma-1 d-flex align-center justify-center"
              >
                <VIcon
                  size="17"
                  icon="tabler-info-circle"
                />
                To add an annotation — right-click on the file
              </VAlert>
            </div>
            <div v-if="isDisplayableFileType" class="d-flex align-center flex-wrap">
              <VSlider
                v-model="pdfScale"
                :max="500"
                :min="100"
                :step="10"
                label="Scale, %"
                hide-details
                class="cr-file-review-scale-slider"
              >
                <template v-slot:append>
                  <VTextField
                    v-model="pdfScale"
                    type="number"
                    density="compact"
                    hide-details
                    variant="outlined"
                    class="pa-1 cr-file-review-scale-text-field"
                  />
                </template>
              </VSlider>
            </div>
          </div>
        </template>
        <VCardText>
          <VOverlay
            v-model="loading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VOverlay>
          <VRow>
            <VCol cols="12" :md="isDisplayableFileType ? 8 : 12" class="scrollable-column">
              <CrFileReviewMainBlock
                v-model:annotations="annotations"
                v-model:file-current-page="fileCurrentPage"
                v-model:file-loading="fileLoading"
                :file-attachment="fileAttachment"
                :pdf-scale="pdfScale"
                :header-height="headerHeight"
                :cr-item-type="crItemType"
                @getAnnotations="getAnnotations"
                @onSetHover="setHover($event.annotation, $event.status)"
              />
            </VCol>
            <VCol v-if="isDisplayableFileType" cols="12" md="4" class="scrollable-column">
              <CrFileReviewAnnotationsBlock
                v-model:annotations="annotations"
                :current-page="fileCurrentPage"
                :has-ordinary-access="hasOrdinaryAccess"
                @onSetHover="setHover($event.annotation, $event.status)"
                @onComment="hasOrdinaryAccess ? (parentAnnotation = $event, showAnnotationCommentDialog = true) : undefined"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </CustomVDialog>
  </div>
</template>

<style lang="scss">
.cr-file-review-dialog {
  .cr-file-review-scale-slider {
    @media (min-width: 400px) {
      min-width: 350px;
    }
    @media (max-width: 399.9px) {
      min-width: calc(100vw - 60px);
    }
    .cr-file-review-scale-text-field {
      width: 70px;
      input.v-field__input {
        padding: 0 5px !important;
      }
    }
  }

  .scrollable-column {
    overflow-y: auto;
    // max-height: display size - header height - top and bottom dialog padding
    max-height: calc(100vh - var(--header-height) - 40px);

    // Disable scroll on smaller screens
    @media (max-width: 959.99px) {
      overflow-y: visible;
      max-height: none;
    }
  }
}
</style>
